import Cache from '@fs/zion-cache'
import { mutate } from 'swr'
import type { PersonId } from '@fs/zion-tree-types'
import axiosInstance from '@fs/zion-axios'
import type { Discussion } from './notes'
import type { SwrResponse } from './useSwrRequest'
import { fetcher, useSwrRequest, updateRequest } from './useSwrRequest'

const getCache = new Cache({ storeName: 'discussions_r9', encrypted: process.env.NODE_ENV === 'production' })
const getUrl = (personId: PersonId | null | undefined): string | null =>
  personId ? `/service/tree/tf/person/${personId}/entityref` : null

const axios = axiosInstance

type EntityRefs = {
  entityRefs: {
    value: {
      type: 'DISCUSSION'
      uri: string // This is the Discussion ID
      id?: string
    }
  }[]
}

/**
 * Reads discussions from tree-data backend and converts it to the format required by the frontend.
 * The `url` should be of the form:
 *   `/service/tree/tf/person/${personId}/entityref`
 */
export async function readDiscussions(url: string, cache?: Cache): Promise<Discussion[] | undefined> {
  const res = await fetcher<EntityRefs>(`${url}?version=2`, cache)
  // we Need to support both uri's "https://api.familysearch.org/discussions/discussion/ds.disc.MMM9-ZZZZ", "ds.disc.MMMW-ZZZZ"
  const ids = res?.entityRefs
    ?.filter((entity) => entity.value.type === 'DISCUSSION')
    .map((e) => e.value.uri.split('/').at(-1))
  if (!ids || ids.length === 0) return []

  const reqs = ids.map((id) => fetcher<Discussion>(`/service/tree/discussions/discussion/${id}`, cache))
  const allDiscussions = await Promise.all<Discussion | undefined>(reqs)

  if (allDiscussions.some((discussion) => discussion === undefined)) return undefined
  return allDiscussions as Discussion[]
}

/**
 * Adds a discussion to tree-data backend.
 * The `url` should be the same as used in `readDiscussions`.
 *
 * Implementation note:
 * This is a rather strange API. First, create a discussion by posting to
 *   `/service/tree/discussions/discussion?productKey=FamilyTree` with the payload:
 *     { title: ${titleText}, firstComment: { text: ${discussionText} }}
 * which creates the discussion and returns the new ID in the response header 'location'.
 * Then add the new discussion to the person entityRefs by posting to
 *   `/service/tree/tf/person/${personId}/entityRef` with the payload:
 *     { family: 'DISCUSSION_FAMILY', value: { type: 'DISCUSSION', id: ${discussionId}, uri: ${discussionId} } }
 */
export async function createDiscussion(url: string, discussion: UnsavedDiscussion): Promise<void> {
  // Create the discussion
  const response = await axios.post('/service/tree/discussions/discussion?productKey=FamilyTree', {
    title: discussion.title,
    firstComment: { text: discussion.text },
  })
  const discussionId = response.headers?.location

  // Attach the discussion to the person's entity refs
  await axios.post(url, {
    family: 'DISCUSSION_FAMILY',
    value: {
      type: 'DISCUSSION',
      id: discussionId,
      uri: discussionId,
    },
  })
}

export type DiscussionsResponse = SwrResponse<Discussion[]> & { discussions: Discussion[] }

/**
 * Get all discussions attached to a person.
 */
export default function useDiscussions(personId: PersonId | null | undefined): DiscussionsResponse {
  const response = useSwrRequest<Discussion[]>(getUrl(personId), getCache, { emulator: readDiscussions })
  return { ...response, discussions: response.data ?? [] }
}

export type UnsavedDiscussion = Omit<Discussion, 'id' | 'commentCount' | 'modifiedDate'>

/**
 * Adds a discussion to the backend.
 * Implementation note:
 * The API for this is rather unusual
 */
export async function addDiscussion(pid: PersonId, discussion: UnsavedDiscussion): Promise<void> {
  const key = getUrl(pid)
  if (!key) return
  await updateRequest(key, undefined, () => createDiscussion(key, discussion))
}

/** Function that can be called to mark discussion data as stale. */
export function mutateDiscussions(personId: PersonId): void {
  mutate(getUrl(personId))
}
